import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useSerpHistoryDetailApi } from '@/common/api/modules/competitorSerp'
import { MediaAnalysisType } from '@/common/model/media/mediaAnalysis'
import { circledNumber } from '@/common/util/number'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import BarChart from '@/components/view/atoms/chart/BarChart'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import MetricItem from '@/components/view/molecules/metric/MetricItem'

import AnalysisActionList from '@/components/container/modules/media/action/AnalysisActionList'

import MediaAnalysisResultPageBase from './MediaAnalysisResultPageBase'

// ---- result_data の型（competitor-serp/analyze の formatted 出力） ----
interface DomainSummary {
  domain: string
  estimated_traffic: number
  keyword_count: number
  top10_count: number
  domain_authority?: number
}
interface KeywordRanking {
  rank?: number
  // SERP実データは "3→3" のような文字列。数値差分ではないため表示にはそのまま使わない
  rank_change?: string
}
interface KeywordItem {
  keyword: string
  search_volume: number
  ad_competition: number
  match_count: number
  rankings: Record<string, KeywordRanking>
}
interface SerpResultData {
  target_domain?: string
  competitor_domains?: string[]
  summaries?: DomainSummary[]
  keywords?: KeywordItem[]
  error?: string
}

const domainCardIndexColor = (index: number): TextColor => {
  switch (index) {
    case 0:
      return TextColor.SORA
    case 1:
      return TextColor.ORANGE
    case 2:
      return TextColor.CAUTION
    case 3:
      return TextColor.PURPLE
    default:
      return TextColor.SECONDARY
  }
}

const DomainCard = ({
  summary,
  isTarget,
  index
}: {
  summary: DomainSummary
  isTarget: boolean
  index: number
}) => (
  <Area borderColor={index === 0 ? 'sora' : undefined}>
    <Heading subText={isTarget ? '自社ドメイン' : `競合${index}`}>{summary.domain}</Heading>
    <AreaContent>
      <ItemAlign>
        <Text size={12} weight='bold'>
          推定月間流入数
        </Text>
        <Text size={24} weight='bold' color={domainCardIndexColor(index)}>
          {(summary.estimated_traffic ?? 0).toLocaleString()}
        </Text>
      </ItemAlign>
      <div className='flex flex-wrap gap-x-4 gap-y-2'>
        <MetricItem label='キーワード数' value={(summary.keyword_count ?? 0).toLocaleString()} />
        <MetricItem label='10位以内' value={(summary.top10_count ?? 0).toLocaleString()} />
        {/* domain_authority は DataForSEO では別APIが必要で常に0のため、値がある場合のみ表示 */}
        {(summary.domain_authority ?? 0) > 0 && (
          <MetricItem label='DA' value={summary.domain_authority ?? 0} />
        )}
      </div>
    </AreaContent>
  </Area>
)

// キーワード別のドメイン順位セル。上位（10位以内）は王冠バッジ、11位以降は数値のみ、圏外は「-」
const RankCell = ({ rank }: { rank?: number }) => {
  if (rank == null) return <Text color={TextColor.SECONDARY}>-</Text>
  if (rank <= 10)
    return (
      <Badge badgeType={BadgeType.SQUARE_CAUTION}>
        <ItemAlign gapX={2} align='center' direction='horizon'>
          <Icon type={IconType.CROWN} color={IconColor.CAUTION} />
          <Text color={TextColor.CAUTION} weight='bold'>
            {rank}
          </Text>
        </ItemAlign>
      </Badge>
    )
  return <Text weight='bold'>{rank}</Text>
}

// SERP実データ分析結果（DataForSEO実データによる検索順位・検索ボリューム）
const MediaAnalysisSerpResultPage = () => {
  const { siteId, historyId } = useParams()
  const sid = Number(siteId)
  // 分析中（status=analyzing）の間は2秒間隔でポーリングし、完了したら自動的に停止する
  const { data, error } = useSerpHistoryDetailApi(
    siteId && historyId ? { site_id: sid, history_id: Number(historyId) } : undefined,
    { refreshInterval: (latest) => (latest?.status === 'analyzing' ? 2000 : 0) }
  )

  const result = (data?.result_data ?? {}) as SerpResultData
  const targetDomain = result.target_domain || ''
  const summaries = result.summaries ?? []
  const keywords = result.keywords ?? []
  const domains = summaries.length
    ? summaries.map((s) => s.domain)
    : [targetDomain, ...(result.competitor_domains ?? [])].filter(Boolean)

  // 統計サマリー
  const ownTop10 = summaries.find((s) => s.domain === targetDomain)?.top10_count ?? 0

  // 全キーワードで自社・競合ともに順位が取れなかった場合。値が0で埋まる画面と
  // 不具合の区別がつかないため、検出できなかったことを明示する
  const noRankingDetected =
    keywords.length > 0 && keywords.every((kw) => (kw.match_count ?? 0) === 0)

  // キーワード別SERP順位一覧の絞り込み（含む／含まない）
  const [includeKw, setIncludeKw] = useState('')
  const [excludeKw, setExcludeKw] = useState('')
  const filteredKeywords = keywords.filter((kw) => {
    const k = kw.keyword.toLowerCase()
    if (includeKw && !k.includes(includeKw.toLowerCase())) return false
    if (excludeKw && k.includes(excludeKw.toLowerCase())) return false
    return true
  })

  // 月間検索数・広告出稿度での並び替え（同一列を再クリックで昇順/降順トグル）
  const [sortKey, setSortKey] = useState<'search_volume' | 'ad_competition' | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const toggleSort = (key: 'search_volume' | 'ad_competition') => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }
  const sortIcon = (key: 'search_volume' | 'ad_competition') =>
    sortKey !== key
      ? IconType.ARROWS_UP_DOWN
      : sortDir === 'asc'
        ? IconType.ARROWS_ASC
        : IconType.ARROWS_DESC
  const sortedKeywords = sortKey
    ? [...filteredKeywords].sort((a, b) => {
        const va = a[sortKey] ?? 0
        const vb = b[sortKey] ?? 0
        return sortDir === 'asc' ? va - vb : vb - va
      })
    : filteredKeywords

  return (
    <MediaAnalysisResultPageBase
      type={MediaAnalysisType.COMPETITOR_SERP}
      data={data}
      error={error}
      targetDomain={targetDomain}
      resultError={result.error}
    >
      {/* 順位が1件も検出できなかった場合の注意書き */}
      {noRankingDetected && (
        <Area bottomMargin={16} bgColor='caution'>
          <AreaContent>
            <Text size={14} weight='bold' color={TextColor.CRITICAL}>
              対象ドメインが検索結果の上位100位以内に検出されませんでした
            </Text>
            <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
              分析した{keywords.length}
              件のキーワードすべてで、自社・競合いずれのドメインも順位が見つかりませんでした。
              流入数・キーワード数・順位はいずれも0件として表示されます。分析対象のサイトURLに誤りがないかご確認ください。
            </Text>
          </AreaContent>
        </Area>
      )}

      {/* 統計サマリー */}
      {keywords.length > 0 && (
        <Area bottomMargin={16}>
          <SummaryItemList shadow>
            <SummaryItem>
              <Text size={12} weight='bold'>
                分析キーワード数
              </Text>
              <Text size={24} weight='bold'>
                {keywords.length.toLocaleString()}
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                SERP機能検出キーワード
              </Text>
              <Text size={24} weight='bold' color={TextColor.SECONDARY}>
                -
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                上位ページ平均文字数
              </Text>
              <Text size={24} weight='bold' color={TextColor.SECONDARY}>
                -
              </Text>
            </SummaryItem>
            <SummaryItem>
              <Text size={12} weight='bold'>
                自社10位以内
              </Text>
              <Text size={24} weight='bold'>
                {ownTop10.toLocaleString()}
              </Text>
            </SummaryItem>
          </SummaryItemList>
        </Area>
      )}

      {/* 改善推奨アクション */}
      <AnalysisActionList siteId={sid} mediaAnalysisId={Number(historyId)} />

      {/* 自社と競合のドメイン比較 */}
      {summaries.length > 0 && (
        <>
          <SummaryHeading>自社と競合のドメイン比較</SummaryHeading>
          <div className='grid grid-cols-1 gap-4 pb-6 sm:grid-cols-2 lg:grid-cols-4'>
            {summaries.map((s, i) => (
              <DomainCard
                key={s.domain}
                summary={s}
                isTarget={s.domain === targetDomain}
                index={i}
              />
            ))}
          </div>
        </>
      )}

      {/* 比較グラフ */}
      {summaries.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>比較グラフ</Heading>
          <div className='flex gap-6'>
            <div className='lg:w-[calc(50%-0.75rem)]'>
              <ChartWrapper>
                <ChartTitle>推定流入数比較</ChartTitle>
                <BarChart
                  labels={domains}
                  items={summaries.map((s) => ({
                    title: s.domain,
                    values: [s.estimated_traffic ?? 0]
                  }))}
                  single
                />
              </ChartWrapper>
            </div>
            <div className='lg:w-[calc(50%-0.75rem)]'>
              <ChartWrapper>
                <ChartTitle>ランクインキーワード数</ChartTitle>
                <BarChart
                  labels={domains}
                  items={[
                    { title: '10位以内', values: summaries.map((s) => s.top10_count ?? 0) },
                    { title: '100位以内', values: summaries.map((s) => s.keyword_count ?? 0) }
                  ]}
                />
              </ChartWrapper>
            </div>
          </div>
        </Area>
      )}

      {/* キーワード別SERP順位一覧 */}
      {keywords.length > 0 && domains.length > 0 && (
        <Area bottomMargin={16}>
          <Heading>キーワード別SERP順位一覧</Heading>
          <AreaContent>
            <div className='flex flex-wrap gap-4'>
              <TextField
                label='含む'
                size={TextBoxSize.MEDIUM}
                value={includeKw}
                placeholder='キーワード'
                onChange={(e) => setIncludeKw(e.target.value)}
              />
              <TextField
                label='含まない'
                size={TextBoxSize.MEDIUM}
                value={excludeKw}
                placeholder='キーワード'
                onChange={(e) => setExcludeKw(e.target.value)}
              />
            </div>
            <table className='w-full'>
              <thead>
                <TableRow head noBorder>
                  <TableHeadItem type='thin'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      キーワード
                    </Text>
                  </TableHeadItem>
                  <TableHeadItem type='thin' width={64}>
                    <Text size={12} color={TextColor.SECONDARY}>
                      一致数
                    </Text>
                  </TableHeadItem>
                  {domains.map((d, i) => (
                    <TableHeadItem key={d} type='thin' width={128}>
                      <Text size={12} color={TextColor.SECONDARY} truncate>
                        {i === 0 ? '自社順位' : `競合${circledNumber(i)}`}
                      </Text>
                      <Text size={10} color={TextColor.SECONDARY} truncate>
                        {d}
                      </Text>
                    </TableHeadItem>
                  ))}
                  <TableHeadItem type='thin' width={128}>
                    <button
                      type='button'
                      className='inline-flex cursor-pointer items-center gap-1'
                      onClick={() => toggleSort('search_volume')}
                    >
                      <Text size={12} color={TextColor.SECONDARY}>
                        月間検索数
                      </Text>
                      <Icon
                        type={sortIcon('search_volume')}
                        color={
                          sortKey === 'search_volume' ? IconColor.EMPHASIS : IconColor.SECONDARY
                        }
                      />
                    </button>
                  </TableHeadItem>
                  <TableHeadItem type='thin' width={128}>
                    <button
                      type='button'
                      className='inline-flex cursor-pointer items-center gap-1'
                      onClick={() => toggleSort('ad_competition')}
                    >
                      <Text size={12} color={TextColor.SECONDARY}>
                        広告出稿度
                      </Text>
                      <Icon
                        type={sortIcon('ad_competition')}
                        color={
                          sortKey === 'ad_competition' ? IconColor.EMPHASIS : IconColor.SECONDARY
                        }
                      />
                    </button>
                  </TableHeadItem>
                </TableRow>
              </thead>
              <tbody>
                {sortedKeywords.map((kw, index) => (
                  <TableRow key={index}>
                    <TableRowItem>
                      <Text weight='bold'>{kw.keyword}</Text>
                    </TableRowItem>
                    <TableRowItem width={64}>
                      <Text>{kw.match_count ?? 0}</Text>
                    </TableRowItem>
                    {domains.map((d) => (
                      <TableRowItem key={d} width={128}>
                        <RankCell rank={kw.rankings?.[d]?.rank} />
                      </TableRowItem>
                    ))}
                    <TableRowItem width={128}>
                      <Text>{(kw.search_volume ?? 0).toLocaleString()}</Text>
                    </TableRowItem>
                    <TableRowItem width={128}>
                      <ItemAlign gapX={4} align='center'>
                        <div className='bg-fill-tertiary h-1.5 w-12 overflow-hidden rounded-full'>
                          <div
                            className='bg-fill-primary-brand h-full rounded-full'
                            style={{
                              width: `${Math.min(Math.round((kw.ad_competition ?? 0) * 100), 100)}%`
                            }}
                          />
                        </div>
                        <Text size={12} color={TextColor.SECONDARY}>
                          {Math.round((kw.ad_competition ?? 0) * 100)}%
                        </Text>
                      </ItemAlign>
                    </TableRowItem>
                  </TableRow>
                ))}
              </tbody>
            </table>
          </AreaContent>
        </Area>
      )}
    </MediaAnalysisResultPageBase>
  )
}

export default MediaAnalysisSerpResultPage
